import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import {
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  ContentType,
  GeneratedPlan,
  GeneratedScript,
  generatedContentApi,
} from 'src/api/generatedContent';
import { useAIAssistantContext } from 'src/hooks/AIAssistant/useAiAssistantContext';
import { formatDate } from 'src/utils/aiGeneratedContent';
import styles from './ContentCard.module.scss';
import ScriptEditDialog from './ScriptEditDialog';

interface ContentCardProps {
  content: GeneratedScript | GeneratedPlan;
  onEdit: (content: GeneratedScript | GeneratedPlan) => void;
  onDelete: (id: string) => void;
  onView: (content: GeneratedScript | GeneratedPlan) => void;
  onViewLinkedScripts?: () => void;
  onViewParentPlan?: () => void;
}

const ContentCard: React.FC<ContentCardProps> = ({
  content,
  onEdit,
  onDelete,
  onView,
  onViewLinkedScripts,
  onViewParentPlan,
}) => {
  const { contentType } = useAIAssistantContext();
  const [scriptEditDialogOpen, setScriptEditDialogOpen] = useState(false);
  const [localContent, setLocalContent] = useState<
    GeneratedScript | GeneratedPlan
  >(content);

  // Update local content when the content prop changes
  useEffect(() => {
    if (content) {
      // Deep check to ensure we're actually updating with new data
      if (JSON.stringify(content) !== JSON.stringify(localContent)) {
        setLocalContent(content);
      }
    }
  }, [content, localContent]);

  const refreshContentData = async () => {
    try {
      if (!localContent || !localContent._id) {
        return;
      }

      if (contentType === ContentType.SCRIPT) {
        const scriptId = (localContent as GeneratedScript)._id;
        try {
          const updatedScript =
            await generatedContentApi.getScriptById(scriptId);
          if (updatedScript) {
            setLocalContent(updatedScript);
          }
        } catch (error) {
          console.error(`Error fetching script ${scriptId}:`, error);
        }
      } else if (contentType === ContentType.CIRCLE_PLAN) {
        const planId = (localContent as GeneratedPlan)._id;
        try {
          const updatedPlan = await generatedContentApi.getPlanById(planId);
          if (updatedPlan) {
            setLocalContent(updatedPlan);
          }
        } catch (error) {
          console.error(`Error fetching plan ${planId}:`, error);
        }
      }
    } catch (error) {
      console.error('Error in refreshContentData:', error);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (
      localContent &&
      !(e.target as HTMLElement).closest('button') &&
      !(e.target as HTMLElement).closest('.MuiIconButton-root')
    ) {
      // Refresh content data before viewing if it's a circle plan
      if (contentType === ContentType.CIRCLE_PLAN) {
        refreshContentData().then(() => {
          onView(localContent);
        });
      } else {
        onView(localContent);
      }
    }
  };

  if (!localContent) {
    return null;
  }

  const getPreviewText = () => {
    if (contentType === ContentType.SCRIPT) {
      const scriptContent = (localContent as GeneratedScript).content;
      if (!scriptContent) {
        return 'No content available';
      }
      return (
        scriptContent.substring(0, Math.min(scriptContent.length, 300)) +
        (scriptContent.length > 300 ? '...' : '')
      );
    } else {
      const plan = localContent as GeneratedPlan;
      const instruction = plan.instruction;
      if (!instruction) {
        return 'No instruction available';
      }

      return (
        instruction.substring(0, Math.min(instruction.length, 300)) +
        (instruction.length > 300 ? '...' : '')
      );
    }
  };

  const handleEdit = () => {
    if (contentType === ContentType.SCRIPT) {
      setScriptEditDialogOpen(true);
    } else {
      // For circle plans, refresh data first
      refreshContentData().then(() => {
        onEdit(localContent);
      });
    }
  };

  const handleScriptUpdated = async () => {
    if (contentType === ContentType.SCRIPT) {
      try {
        const scriptId = (localContent as GeneratedScript)._id;
        const updatedScript = await generatedContentApi.getScriptById(scriptId);
        if (updatedScript) {
          setLocalContent(updatedScript);
          onEdit(updatedScript);
        }
      } catch (error) {
        console.error('Error fetching updated script:', error);
      }
    }
  };

  return (
    <>
      <Card className={styles.contentCard} onClick={handleCardClick}>
        <CardContent>
          <div className={styles.contentHeader}>
            <Typography variant="h6" className={styles.contentTitle}>
              {localContent.title}
            </Typography>
          </div>

          <div className={styles.contentPreview}>{getPreviewText()}</div>

          <div className={styles.contentFooter}>
            <div className={styles.contentMeta}>
              <span>Created: {formatDate(localContent.createdAt || '')}</span>
              {localContent.createdAt !== localContent.updatedAt && (
                <span>Updated: {formatDate(localContent.updatedAt || '')}</span>
              )}
            </div>

            <div className={styles.contentActions}>
              {onViewLinkedScripts && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={onViewLinkedScripts}
                  sx={{ textTransform: 'none' }}
                >
                  View Linked Scripts
                </Button>
              )}

              {onViewParentPlan && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={onViewParentPlan}
                  sx={{ textTransform: 'none' }}
                >
                  View Parent Plan
                </Button>
              )}

              <Tooltip title="Edit Content">
                <IconButton
                  size="small"
                  onClick={handleEdit}
                  className={styles.actionButton}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete Content">
                <IconButton
                  size="small"
                  onClick={() => onDelete(localContent._id)}
                  className={styles.actionButton}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Script Edit Dialog */}
      <ScriptEditDialog
        open={scriptEditDialogOpen}
        onClose={() => setScriptEditDialogOpen(false)}
        script={localContent as GeneratedScript}
        onScriptUpdated={handleScriptUpdated}
      />
    </>
  );
};

export default ContentCard;
